<template>
  <section class="plans" ref="root" aria-label="Paquetes para crecer tu negocio">
    <!-- Encabezado -->
    <header class="plans__head" ref="headRef">
      <h2 class="plans__eyebrow">Paquetes pensados para negocios</h2>
      <h3 class="plans__title">
        Aplicamos lo que <span class="mark">ya nos funcionó</span> para atraer clientes reales.
      </h3>
      <p class="plans__sub">
        Tú enfócate en tu negocio; nosotros armamos la máquina: <strong>web que convierte</strong>,
        <strong>chatbot básico</strong> que no deja en visto y, cuando estés listo, una <strong>IA inteligente</strong> que
        atiende WhatsApp como si fueras tú (Fase 2, servicio aparte).
      </p>
      <ul class="plans__benefits" ref="benefitsRef">
        <li>✔️ Implementación guiada y lista para usar</li>
        <li>✔️ Diferencia clara: Chatbot básico (sin IA) vs IA avanzada en WhatsApp</li>
        <li>✔️ Reportes simples que muestran resultados</li>
      </ul>
    </header>

    <!-- Tarjetas -->
    <div class="plans__grid" ref="gridRef">
      <!-- 1) Web + Chatbot (básico, sin IA) -->
      <article class="card" ref="card1">
        <div class="card__badge">Popular</div>
        <h4 class="card__title">Web + Chatbot</h4>
        <p class="card__tagline">Página que convierte + contestador básico 24/7</p>
        <ul class="card__list">
          <li>Landing o sitio profesional (1–5 secciones)</li>
          <li>Botones directos de contacto</li>
          <li><strong>Chatbot básico (sin IA)</strong> con respuestas predefinidas (FAQs)</li>
          <li>Integración básica a email / CRM</li>
          <li>SEO técnico y performance alto</li>
        </ul>
        <div class="card__price">
          <span class="price__old">Antes $1,550 USD</span><br />
          <span class="price__big"><strong>Pago único: desde $850 USD</strong></span>
          <span class="price__note">Incluye chatbot <strong>sin IA</strong>. La IA en WhatsApp es <strong>otro servicio</strong> (Fase 2).</span>
          <span class="price__note">Gastos externos opcionales: dominio/hosting (aprox $10–20 USD/mes).</span>
        </div>
        <div class="card__ctas">
          <a :href="demoHref" target="_blank" rel="noopener" class="btn btn--primary">Ver demo</a>
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--ghost">Quiero cotizar</a>
        </div>
      </article>

      <!-- 2) Redes & Ads -->
      <article class="card" ref="card2">
        <div class="card__badge card__badge--pro">Recomendado</div>
        <h4 class="card__title">Redes & Ads</h4>
        <p class="card__tagline">Contenido + anuncios que atraen clientes</p>
        <ul class="card__list">
          <li>Gestión de redes (publicaciones profesionales constantes)</li>
          <li>Meta Ads (Facebook/Instagram) orientado a leads</li>
          <li>Google Ads para búsqueda local</li>
          <li>Copy y segmentación probados</li>
          <li>Reportes mensuales simples</li>
          <li>Contestador automático básico en redes (sin IA)</li>
        </ul>
        <div class="card__price">
          <span class="price__big"><strong>desde $350 USD/mes</strong></span>
          <span class="price__note">Inversión en medios (ads) se paga aparte.</span>
        </div>
        <div class="card__ctas">
          <button class="btn btn--primary" @click="showRedesModal = true">Más detalles</button>
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--ghost">Pedir propuesta</a>
        </div>
      </article>

      <!-- 3) IA Inteligente (Fase 2) + WhatsApp -->
      <article class="card" ref="card3">
        <div class="card__badge card__badge--ia">Fase 2</div>
        <h4 class="card__title">IA Inteligente + WhatsApp</h4>
        <p class="card__tagline">Responde como tú, 24/7, en tu número de WhatsApp</p>
        <ul class="card__list">
          <li><strong>Conexión con WhatsApp Business API</strong> (proveedor oficial)</li>
          <li>IA que entiende intención y responde en tu tono</li>
          <li>Entrenamiento con tu web, PDFs, catálogos y políticas</li>
          <li>Handoff a humano cuando se necesita</li>
          <li>Embudo de ventas: captura datos, califica y agenda</li>
          <li>Métricas: tiempos de respuesta, leads y conversiones</li>
        </ul>
        <div class="card__price">
          <span class="price__big"><strong>Setup único: $490 USD</strong></span><br />
          <span class="price__big"><strong>Mensualidad: desde $149 USD/mes</strong></span>
          <span class="price__note"><strong>WhatsApp (Meta/proveedor) cobra aparte por conversación.</strong></span>
          <details class="price__details">
            <summary>¿Cuánto cobra WhatsApp? (ejemplos claros)</summary>
            <div class="price__examples">
              <p><strong>Se cobra por conversación</strong> (no por cada mensaje). Depende del país y tipo de conversación.</p>
              <ul>
                <li>Con <strong>100 conversaciones/mes</strong> → WhatsApp aprox <strong>$1–$15 USD</strong></li>
                <li>Con <strong>500 conversaciones/mes</strong> → WhatsApp aprox <strong>$5–$75 USD</strong></li>
              </ul>
              <p><em>Ejemplo total (100 conversaciones/mes):</em> $149 (nuestro plan) + $1–$15 (WhatsApp) = <strong>$150–$164 USD/mes</strong>.</p>
            </div>
          </details>
        </div>
        <div class="card__ctas">
          <button class="btn btn--primary" @click="showIaModal = true">Qué incluye la IA</button>
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--ghost">Quiero la Fase 2</a>
        </div>
      </article>
    </div>

    <!-- Aviso general -->
    <p class="plans__foot">
      * La <strong>IA Inteligente</strong> es un servicio aparte (Fase 2). El paquete “Web + Chatbot” incluye únicamente
      un chatbot de reglas predefinidas (sin IA). Podemos empezar con el chatbot y, cuando estés listo,
      <strong>activar la IA en WhatsApp</strong>.
    </p>

    <!-- MODAL: Redes & Ads -->
    <div v-if="showRedesModal" class="modal" @click.self="showRedesModal = false">
      <div class="modal__content" role="dialog" aria-modal="true" aria-label="Detalles de Redes & Ads">
        <button class="modal__close" @click="showRedesModal = false" aria-label="Cerrar">×</button>
        <h4 class="modal__title">Redes & Ads — Detalles</h4>
        <p class="modal__subtitle">Lo explicamos fácil y sin tecnicismos:</p>
        <ul class="modal__list">
          <li><strong>Gestión de redes:</strong> publicaciones profesionales y constantes para dar confianza.</li>
          <li><strong>Meta Ads orientado a leads:</strong> anuncios pensados para generar conversaciones reales.</li>
          <li><strong>Google Ads local:</strong> apareces cuando te buscan con intención de compra.</li>
          <li><strong>Copy y segmentación:</strong> textos que persuaden + público correcto.</li>
          <li><strong>Reportes simples:</strong> alcance, clics, mensajes y prospectos.</li>
          <li><strong>Contestador automático básico:</strong> sin IA, flujo guiado con respuestas rápidas.</li>
        </ul>
        <div class="modal__cta">
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--primary">Quiero cotizar</a>
        </div>
      </div>
    </div>

    <!-- MODAL: IA Inteligente -->
    <div v-if="showIaModal" class="modal" @click.self="showIaModal = false">
      <div class="modal__content" role="dialog" aria-modal="true" aria-label="Detalles de IA Inteligente + WhatsApp">
        <button class="modal__close" @click="showIaModal = false" aria-label="Cerrar">×</button>
        <h4 class="modal__title">IA Inteligente + WhatsApp — ¿Qué incluye?</h4>
        <p class="modal__subtitle">Aclaraciones clave y alcance de la Fase 2 (no está incluida con la web):</p>
        <ul class="modal__list">
          <li><strong>No viene incluida</strong> en “Web + Chatbot”. Es la <strong>Fase 2</strong>.</li>
          <li><strong>Conecta con WhatsApp Business API</strong> (a través de un proveedor oficial).</li>
          <li><strong>Entrenamos la IA</strong> con tu web, documentos y preguntas reales de clientes.</li>
          <li><strong>Respuestas naturales</strong> (entiende intención, mantiene contexto y tono de marca).</li>
          <li><strong>Handoff a humano</strong> cuando la conversación lo requiere.</li>
          <li><strong>Reportes</strong> de performance (SLA de respuesta, leads, agendamientos).</li>
        </ul>
        <div class="modal__separator"></div>
        <div class="modal__pricing">
          <p class="modal__price-line"><strong>Setup (una sola vez):</strong> $490 USD</p>
          <p class="modal__price-line"><strong>Mensualidad:</strong> desde $149 USD/mes</p>
          <p class="modal__price-line"><strong>WhatsApp cobra aparte por conversación</strong> (Meta/proveedor). Con 100 conversaciones/mes suele ser <strong>$1–$15 USD</strong>.</p>
        </div>
        <div class="modal__cta">
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--primary">Quiero la Fase 2</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'PaquetesCrecimiento',
  props: {
    demoHref: { type: String, default: 'https://calendly.com/' },
    waPhone:  { type: String, default: '+16126005144' },
    waText:   { type: String, default: 'Hola, me interesa un paquete para atraer clientes.' }
  },
  setup (props) {
    const root = ref(null)
    const headRef = ref(null)
    const benefitsRef = ref(null)
    const gridRef = ref(null)
    const card1 = ref(null)
    const card2 = ref(null)
    const card3 = ref(null)

    const showRedesModal = ref(false)
    const showIaModal = ref(false)

    const waHref = computed(() => {
      const num = props.waPhone.replace(/\D/g, '')
      return `https://wa.me/${num}?text=${encodeURIComponent(props.waText)}`
    })

    let ctx
    onMounted(() => {
      ctx = gsap.context(() => {
        // Estado inicial
        gsap.set([headRef.value, benefitsRef.value], { autoAlpha: 0, y: 20 })
        gsap.set([card1.value, card2.value, card3.value], { autoAlpha: 0, y: 28, rotateX: 6 })

        // Header
        gsap.timeline({
          scrollTrigger: { trigger: root.value, start: 'top 80%', once: true }
        })
        .to(headRef.value,     { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' })
        .to(benefitsRef.value, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '-=0.2')

        // Tarjetas
        const cards = [card1.value, card2.value, card3.value]
        cards.forEach((c) => {
          gsap.timeline({
            scrollTrigger: { trigger: c, start: 'top 85%', once: true }
          })
          .to(c, { autoAlpha: 1, y: 0, rotateX: 0, duration: 0.6, ease: 'power3.out' })
          .from(c.querySelectorAll('.card__title, .card__tagline'), { y: 14, autoAlpha: 0, duration: 0.35, stagger: 0.06, ease: 'power2.out' }, '-=0.25')
          .from(c.querySelectorAll('.card__list li'), { y: 10, autoAlpha: 0, duration: 0.25, stagger: 0.05, ease: 'power1.out' }, '-=0.15')
          .from(c.querySelector('.card__price'), { y: 10, autoAlpha: 0, duration: 0.3, ease: 'power2.out' }, '-=0.1')
          .from(c.querySelectorAll('.btn'), { y: 8, autoAlpha: 0, duration: 0.25, stagger: 0.05, ease: 'back.out(1.6)' }, '-=0.1')

          // Parallax sutil
          gsap.to(c, {
            y: -10,
            scrollTrigger: { trigger: c, start: 'top bottom', end: 'bottom top', scrub: 0.5 }
          })
        })
      }, root.value)
    })

    onBeforeUnmount(() => { ctx && ctx.revert() })

    return { root, headRef, benefitsRef, gridRef, card1, card2, card3, waHref, showRedesModal, showIaModal }
  }
}
</script>

<style scoped>
.plans{
  background:#0f1117;
  color:#fff;
  padding:48px 0 56px;
  font-family:'Poppins',system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;
}
.plans__head{
  max-width:1100px; margin:0 auto; padding:0 24px; text-align:center;
}
.plans__eyebrow{
  margin:0 0 6px; color:#9aa4b2; font-weight:700; font-size:14px; letter-spacing:.6px; text-transform:uppercase;
}
.plans__title{
  margin:0 0 10px; font-size:clamp(28px,4.6vw,44px); line-height:1.15; font-weight:900;
}
.mark{ background:#6f2cff; padding:2px 8px; border-radius:8px; }
.plans__sub{ margin:0 auto 12px; max-width:780px; color:#cfd6e1; font-size:16px; }
.plans__benefits{
  list-style:none; padding:0; margin:12px auto 0; max-width:760px; display:flex; gap:16px; flex-wrap:wrap; justify-content:center;
}
.plans__benefits li{
  background:#1a1f2b; color:#dbe2ee; border:1px solid #22283a; padding:8px 12px; border-radius:999px; font-weight:600; font-size:13px;
}

/* GRID: 3 columnas en desktop */
.plans__grid{
  max-width:1200px; margin:24px auto 0; padding:0 24px;
  display:grid; grid-template-columns:repeat(3,1fr); gap:20px;
}

.card{
  position:relative; background:#121828; border:1px solid #22283a; border-radius:18px; padding:22px 20px 20px;
  box-shadow:0 14px 36px rgba(0,0,0,.25);
}
.card__badge{
  position:absolute; top:14px; right:14px; font-size:11px; font-weight:800; letter-spacing:.6px; text-transform:uppercase;
  background:#22283a; color:#cfd6e1; padding:6px 8px; border-radius:999px;
}
.card__badge--pro{ background:#6f2cff; color:#fff; }
.card__badge--ia{ background:#00c853; color:#0b1f13; } /* verde para “Fase 2” */

.card__title{ margin:0; font-size:24px; font-weight:900;color:#fff;  }
.card__tagline{ margin:4px 0 10px; color:#cbd2df; font-size:14px; }

.card__list{ list-style:none; padding:0; margin:0 0 14px; display:grid; gap:8px; }
.card__list li{
  display:flex; gap:8px; align-items:flex-start; color:#dee6f3; font-size:14px;
}
.card__list li::before{ content:'•'; color:#6f2cff; margin-top:2px; }

.card__price{ margin:4px 0 12px; }
.price__big{ font-size:18px; color:#fff; }
.price__old{ font-size:18px; color:#ffffff; text-decoration: line-through; }
.price__note{ display:block; color:#9aa4b2; font-size:12px; }
.price__details summary{
  margin-top:8px; cursor:pointer; color:#e3e8ff; font-weight:800; font-size:13px;
}
.price__examples{ margin-top:8px; background:#1a1f2b; border:1px solid #22283a; border-radius:10px; padding:10px; color:#dbe2ee; }
.price__examples ul{ margin:8px 0; padding-left:18px; }

.card__ctas{ display:flex; gap:10px; flex-wrap:wrap; }
.btn{
  appearance:none; border:0; border-radius:10px; padding:12px 16px; font-weight:800; font-size:14px; cursor:pointer; text-decoration:none;
}
.btn--primary{
  background:linear-gradient(180deg,#7a2bff,#5720c9); color:#fff; box-shadow:0 10px 18px rgba(87,32,201,.35);
}
.btn--ghost{
  background:transparent; color:#e6ebf5; border:1px solid #2b3452;
}

.plans__foot{
  margin:18px auto 0; max-width:980px; text-align:center; color:#9aa4b2; font-size:13px; padding:0 24px;
}

/* Modal */
.modal{
  position:fixed; inset:0; background:rgba(0,0,0,.6);
  display:flex; align-items:center; justify-content:center; z-index:9999; padding:24px;
}
.modal__content{
  position:relative;
  background:#121828; border:1px solid #22283a; border-radius:18px;
  max-width:720px; width:100%; padding:22px 20px;
  box-shadow:0 18px 44px rgba(0,0,0,.35);
}
.modal__close{
  position:absolute; right:18px; top:12px; background:transparent; border:0; color:#cfd6e1; font-size:24px; cursor:pointer;
}
.modal__title{ margin:0 0 6px; font-size:22px; font-weight:900; }
.modal__subtitle{ margin:0 0 12px; color:#cbd2df; }
.modal__list{ list-style:none; padding:0; margin:0 0 14px; display:grid; gap:10px; }
.modal__list li{ color:#dee6f3; font-size:14px; line-height:1.5; }
.modal__separator{ height:1px; background:#22283a; margin:12px 0; }
.modal__pricing .modal__price-line{ margin:6px 0; color:#e8ecf7; font-weight:700; }

@media (max-width: 1024px){
  .plans__grid{ grid-template-columns:repeat(2,1fr); }
}
@media (max-width: 820px){
  .plans__grid{ grid-template-columns:1fr; }
}
</style>
